import { CircleCheckBig, Printer, X } from "lucide-react";
import { formatCurrency, formatDateTime } from '../Helpers';
import { generateReceiptPDF } from '../utils/receiptPDF';

const Loader = () => (
    <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500"></div>
);

const StatCard = ({ title, value, icon: Icon, color }) => {
    const colorClasses = {
        green: 'bg-green-100 text-green-600',
        blue: 'bg-blue-100 text-blue-600',
        purple: 'bg-purple-100 text-purple-600',
        red: 'bg-red-100 text-red-600',
        yellow: 'bg-yellow-100 text-yellow-600',
    };

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center space-x-4">
                <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
                    <Icon className="h-6 w-6" />
                </div>
                <div className="flex-1">
                    <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">{title}</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
                </div>
            </div>
        </div>
    );
};

const InputField = ({ label, icon: Icon, ...props }) => (
    <div className={props.className}>
        <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
        <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Icon className="h-5 w-5 text-gray-400" />
            </div>
            <input
                {...props}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            />
        </div>
    </div>
);

const SaleCompletion = ({ onConfirm }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="p-8 bg-white rounded-xl shadow-lg max-w-md w-full mx-4">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">Sale completed!</h2>
            <div className="flex justify-center space-x-4">
                <button
                    onClick={onConfirm}
                    className="py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition duration-150 ease-in-out"
                >
                    <CircleCheckBig />
                </button>
            </div>
        </div>
    </div>
);

const SaleDetails = ({ onClose, saleDetail, shopName = 'StoreFlow' }) => {
    const handlePrintBill = () => {
        const { pdf, filename } = generateReceiptPDF(saleDetail, shopName);
        pdf.save(filename);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="p-4 sm:p-8 bg-white rounded-xl shadow-lg max-w-xl w-full max-h-[90vh] overflow-y-auto">
                <h2 className="text-xl font-bold text-gray-800 mb-1">Transaction ID: <span className="font-mono font-thin">{saleDetail.id}</span></h2>
                <p className="text-sm text-gray-500 mb-4">{formatDateTime(saleDetail.saleDate)}</p>
                <p className="text-sm mb-4">Name: <span className="text-gray-500">{saleDetail.customer?.name || '-'}</span></p>
                <p className="text-sm mb-4">Contact: <span className="text-gray-500">{saleDetail.customer?.phone || '-'}</span></p>
                <p className="text-sm mb-4">Payment: <span className="text-gray-500 capitalize">{saleDetail.paymentMethod || 'Cash'}</span></p>
                <div className="border-t py-4 sm:py-6">
                    {/* Desktop Table View */}
                    <div className="hidden sm:block overflow-x-auto">
                        <table className="w-full text-left rounded-xl overflow-hidden min-w-[400px]">
                            <thead className="bg-gray-200 border-b">
                                <tr className="rounded-lg">
                                    <th className="p-2 sm:p-4 font-medium text-gray-600 text-sm">Item</th>
                                    <th className="p-2 sm:p-4 font-medium text-gray-600 text-center text-sm">Quantity x Price</th>
                                    <th className="p-2 sm:p-4 font-medium text-gray-600 text-right text-sm">Amount</th>
                                </tr>
                            </thead>
                            <tbody className="space-y-2 bg-gray-100 rounded-lg p-3">
                                {saleDetail.items.map((item, index) => (
                                    <tr key={index} className="rounded-lg border-t">
                                        <td className="p-2 sm:p-4 font-medium text-gray-600 text-sm">{item.itemName}</td>
                                        <td className="p-2 sm:p-4 font-medium text-gray-600 text-center text-sm">{item.quantity} x {formatCurrency(item.sellingPrice)}</td>
                                        <td className="p-2 sm:p-4 font-medium text-gray-600 text-right text-sm">
                                            {formatCurrency(item.quantity * item.sellingPrice)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile Card View */}
                    <div className="sm:hidden space-y-3">
                        {saleDetail.items.map((item, index) => (
                            <div key={index} className="bg-gray-100 rounded-lg p-3">
                                <div className="flex items-center justify-between mb-2">
                                    <p className="font-medium text-gray-900 text-sm">{item.itemName}</p>
                                    <p className="font-bold text-gray-900 text-sm">
                                        {formatCurrency(item.quantity * item.sellingPrice)}
                                    </p>
                                </div>
                                <div className="flex items-center justify-between text-xs text-gray-600">
                                    <span>{item.quantity} x {formatCurrency(item.sellingPrice)}</span>
                                    {/* <span>each</span> */}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="flex justify-end mb-6">
                    <div className="w-fit">
                        <p className="text-center align-top text-gray-600 text-sm mb-2">Total Amount</p>
                        <p className="text-center align-top">
                            <div className="bg-green-50 rounded-lg p-3 inline-block">
                                <p className="text-2xl font-bold text-green-600">{formatCurrency(saleDetail.totalAmount)}</p>
                            </div>
                        </p>
                    </div>
                </div>
                <div className="flex flex-col sm:flex-row justify-center space-y-3 sm:space-y-0 sm:space-x-4">
                    <button
                        onClick={onClose}
                        className="py-2 px-4 border border-transparent text-sm font-medium text-black border-black hover:bg-black hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition duration-150 ease-in-out rounded-3xl flex items-center justify-center"
                    >
                        <X className="h-5" />
                        <p>Close</p>
                    </button>
                    <button
                        onClick={handlePrintBill}
                        className="py-2 px-4 border border-transparent text-sm font-medium text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition duration-150 ease-in-out rounded-3xl flex items-center justify-center"
                    >
                        <Printer className="h-5 mr-1" />
                        <p> Print Bill</p>
                    </button>
                </div>
            </div>
        </div>
    );
};


function LogoutConfirmation({ onConfirm, onCancel }) {
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="p-8 bg-white rounded-xl shadow-lg max-w-md w-full mx-4">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">Are you sure you want to logout?</h2>
                <div className="flex justify-end space-x-4">
                    <button
                        onClick={onCancel}
                        className="py-2 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-150 ease-in-out"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        className="py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition duration-150 ease-in-out"
                    >
                        Logout
                    </button>
                </div>
            </div>
        </div>
    );
}

export { StatCard, InputField, LogoutConfirmation, SaleCompletion, Loader, SaleDetails };